import { useHandler } from "@redotech/react-util/hook";
import { SegmentNumberOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoCustomerReturnRateSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

function segmentNumberOperatorToText(): Record<SegmentNumberOperator, string> {
  return {
    [SegmentNumberOperator.LESS_THAN]: t("less than"),
    [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: t("less than or equal to"),
    [SegmentNumberOperator.EQUAL]: t("equal to"),
    [SegmentNumberOperator.GREATER_THAN]: t("greater than"),
    [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: t(
      "greater than or equal to",
    ),
  };
}

export const CustomerReturnRateInspectorContent = memo(
  function CustomerReturnRateInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCustomerReturnRateSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("Customer's return rate is")}{" "}
          {segmentNumberOperatorToText()[segment.operator]}{" "}
          {segment.returnRatePercentage}%
        </Text>
      </Flex>
    );
  },
);

export const EditableCustomerReturnRateInspectorContent = memo(
  function EditableCustomerReturnRateInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCustomerReturnRateSegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentNumberOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleReturnRateChange = useHandler(
      (returnRatePercentage: number) => {
        const updatedSegment = { ...segment, returnRatePercentage };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Customer's return rate")}
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentNumberOperator>
            availableOptions={Object.values(SegmentNumberOperator)}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentNumberOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <RedoIncrementDecrement
          max={100}
          min={0}
          setValue={handleReturnRateChange}
          size="small"
          step={1}
          suffix="%"
          value={segment.returnRatePercentage}
        />
      </>
    );
  },
);
